import { Files, ListChecks, Car, Stethoscope, AlertTriangle, Quote } from "lucide-react";

// Plain-English explainer for the DQ file, MVR & ME certificate (guide Step/Mission 7).
export default function DqFileExplainer() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8" data-testid="dq-file-explainer">
      <div className="flex items-center gap-2 mb-3">
        <Files className="w-4 h-4 text-brand" />
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Plain-English explainer</span>
      </div>
      <h3 className="font-heading text-2xl font-black tracking-tight text-slate-900">The Driver Qualification (DQ) file</h3>
      <p className="mt-3 text-slate-700 leading-relaxed">
        A DQ file is a required folder of documents you keep for <strong>every driver — including yourself</strong>. It's
        one of the first things a DOT auditor asks for. Here's what goes in it, plus the two documents people most often
        get wrong.
      </p>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <ListChecks className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">What goes in it</span>
          </div>
          <ul className="text-sm text-slate-700 leading-relaxed list-disc pl-4 space-y-1">
            <li>Driver's employment application</li>
            <li>CDL info &amp; verification</li>
            <li>Road-test certificate (or equivalent)</li>
            <li>MVR + signed annual review</li>
            <li>Current medical (ME) certificate</li>
            <li>Clearinghouse query records</li>
          </ul>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Car className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">MVR</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Your <strong>Motor Vehicle Record</strong> — the official state driving history (tickets, crashes, license
            status). Pull it from the DMV when you hire and <strong>at least once a year</strong> after, then keep it
            plus a signed annual review in the file.
          </p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Stethoscope className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">ME certificate — how to file</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Get a DOT physical from an examiner on FMCSA's <strong>National Registry</strong>. They issue your Medical
            Examiner's Certificate ("med card"). File a copy in the DQ file, carry one in the truck, and make sure it's
            reported to your state so it lands on your CDL record. Renew before it expires (usually every 24 months).
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-xl bg-white border-l-4 border-amber-500 p-5">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900 leading-relaxed">
          The two that sink new carriers: an <strong>expired med card</strong> and a <strong>missing annual MVR
          review</strong>. Both take minutes to handle but are instant, entirely avoidable audit findings.
        </p>
      </div>

      <div className="mt-5 rounded-xl bg-brand/5 border border-brand/20 p-5">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-full bg-brand flex items-center justify-center shrink-0">
            <Quote className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Mentor tip</span>
        </div>
        <p className="text-sm text-slate-800 leading-relaxed">
          Set calendar reminders for the med-card expiration and the annual MVR pull, and keep the whole DQ file digital
          so you can hand an auditor a clean PDF in seconds. This isn't legal advice — confirm against FMCSA §391.51.
        </p>
      </div>
    </div>
  );
}
